import type { FormValues } from "@/widgets/classic-form/form";
import { getEmptyFormValues } from "@/widgets/classic-form/form";
import type { FormMetadataResponse, OtherFieldGroup } from "@/entities/form-metadata";
import { FormLevelFeatureType, GroupType } from "@/entities/form-metadata";
import {
    populateEventFields,
    populateFamilyMemberFields,
    populateHierarchicalFields,
    populateNameFields,
    populateOtherFields,
    populateSpecialFields,
} from "./groupTypesMappers";

export const populateFormValuesFromMetadata = (options: {
    isEditForm: boolean;
    formMetadata: FormMetadataResponse;
    sourceFormValues: FormValues;
    matchAllTermsExactly: boolean;
}): FormValues => {
    const { isEditForm, formMetadata, sourceFormValues, matchAllTermsExactly } = options;

    const formValues: FormValues = getEmptyFormValues();
    const categoryBucketFeature = formMetadata.features.find(
        (feature) => feature.featureType === FormLevelFeatureType.CategoryBuckets,
    );
    const showUnviewedRecordsOnlyFeature = formMetadata.features.find(
        (feature) => feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly,
    );

    if (categoryBucketFeature) formValues.types = sourceFormValues.types;
    if (showUnviewedRecordsOnlyFeature) formValues.showUnviewedRecordsOnly = sourceFormValues.showUnviewedRecordsOnly;
    formValues.recordSubject = sourceFormValues.recordSubject;
    // priority/location are always pass-through from the URL-derived dataModel regardless of whether
    // CollectionFocus feature is present — they are serialized by the canonicalizer unconditionally.
    formValues.priority = sourceFormValues.priority;
    formValues.location = sourceFormValues.location;

    formMetadata.fieldGroupsAndFeatureContainers?.forEach((fieldGroupOrFeature) => {
        if (!("groupType" in fieldGroupOrFeature)) return;

        if (fieldGroupOrFeature.groupType === GroupType.Name) {
            populateNameFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesNameData: formValues.name,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Hierarchical) {
            populateHierarchicalFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Event) {
            populateEventFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                isEditForm,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.FamilyMember) {
            populateFamilyMemberFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesFamilyData: formValues.family,
                isEditForm,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Other) {
            populateOtherFields({
                fieldGroup: fieldGroupOrFeature as OtherFieldGroup,
                dataModel: sourceFormValues,
                formValuesFieldsData: formValues.fields,
                matchAllTermsExactly,
            });
        } else if (fieldGroupOrFeature.groupType === GroupType.Special) {
            populateSpecialFields({
                fieldGroup: fieldGroupOrFeature,
                dataModel: sourceFormValues,
                formValuesEventsData: formValues.events,
                isEditForm,
            });
        }
    });

    return formValues;
};
